// CANONICAL COPY (GH #7115). src/constants/frameworks/iso22301.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

// ISO 22301:2019 — Security and resilience — Business continuity management
// systems — Requirements. Auditable clauses 4–10 (clauses 1–3 cover scope,
// normative references and terms and are not auditable).
//
// Mapped at second-level clause granularity (e.g. 8.2, 8.3). Sub-clauses roll
// up into their parent for compliance purposes. Where a clause spans multiple
// TCF controls, multiple mapping rows share the same externalId — the last
// entry per externalId is used for scfLookup (primary TCF), while every row
// contributes to controlToFrameworks and externalRefs.

const FRAMEWORK_ISO22301 = {
  id: 'fw-iso22301',
  type: 'standard',
  name: 'ISO 22301:2019',
  shortName: 'ISO 22301',
  abbrev: 'I22301',
  description:
    'Business continuity management system (BCMS) requirements — 26 mandatory clauses across Clauses 4–10 covering context, leadership, planning, support, operation (including business impact analysis, continuity strategies and exercise programme), performance evaluation and improvement.',
  color: '#10b981',
  version: '2019',
  sectionKeyDepth: 1,
  sections: [
    { key: '4', name: 'Context of the organization' },
    { key: '5', name: 'Leadership' },
    { key: '6', name: 'Planning' },
    { key: '7', name: 'Support' },
    { key: '8', name: 'Operation' },
    { key: '9', name: 'Performance evaluation' },
    { key: '10', name: 'Improvement' },
  ],
  mappings: [
    // ── Clause 4 — Context of the organization ─────────────────────────
    {
      externalId: 'ISO22301-4.1',
      scfId: 'SCF-MSS-001',
      ref: 'Clause 4.1',
      section: '4',
      title: 'Understanding the organization and its context',
    },
    {
      externalId: 'ISO22301-4.2',
      scfId: 'SCF-GOV-009',
      ref: 'Clause 4.2',
      section: '4',
      title: 'Understanding the needs and expectations of interested parties',
    },
    {
      primary: true,
      externalId: 'ISO22301-4.2',
      scfId: 'SCF-MSS-001',
      ref: 'Clause 4.2',
      section: '4',
      title: 'Understanding the needs and expectations of interested parties',
    }, // primary
    {
      externalId: 'ISO22301-4.3',
      scfId: 'SCF-MSS-001',
      ref: 'Clause 4.3',
      section: '4',
      title: 'Determining the scope of the BCMS',
    },
    {
      externalId: 'ISO22301-4.4',
      // The harmonised-structure requirement to establish and maintain the
      // management system, not an operational control. It mapped to
      // SCF-BCM-001 (keeping information security intact during a disruption,
      // ISO 27002 A.5.29), which is a different thing and left the clause
      // answering on the wrong evidence. ISO 27001 4.4 and ISO 42001 4.4 both
      // map here (GH #6844).
      scfId: 'SCF-MSS-001',
      ref: 'Clause 4.4',
      section: '4',
      title: 'Business continuity management system',
    },

    // ── Clause 5 — Leadership ──────────────────────────────────────────
    {
      externalId: 'ISO22301-5.1',
      scfId: 'SCF-GOV-004',
      ref: 'Clause 5.1',
      section: '5',
      title: 'Leadership and commitment',
    },
    {
      externalId: 'ISO22301-5.2',
      scfId: 'SCF-GOV-001',
      ref: 'Clause 5.2',
      section: '5',
      title: 'Business continuity policy',
    },
    {
      externalId: 'ISO22301-5.3',
      scfId: 'SCF-GOV-002',
      ref: 'Clause 5.3',
      section: '5',
      title: 'Roles, responsibilities and authorities',
    },

    // ── Clause 6 — Planning ────────────────────────────────────────────
    {
      externalId: 'ISO22301-6.1',
      scfId: 'SCF-RSK-001',
      ref: 'Clause 6.1',
      section: '6',
      title: 'Actions to address risks and opportunities',
    },
    {
      externalId: 'ISO22301-6.2',
      scfId: 'SCF-RSK-002',
      ref: 'Clause 6.2',
      section: '6',
      title: 'Business continuity objectives and planning to achieve them',
    },
    {
      externalId: 'ISO22301-6.3',
      scfId: 'SCF-MSS-004',
      ref: 'Clause 6.3',
      section: '6',
      title: 'Planning changes to the BCMS',
    },

    // ── Clause 7 — Support ─────────────────────────────────────────────
    {
      externalId: 'ISO22301-7.1',
      scfId: 'SCF-MSS-002',
      ref: 'Clause 7.1',
      section: '7',
      title: 'Resources',
    },
    {
      externalId: 'ISO22301-7.2',
      scfId: 'SCF-PPL-003',
      ref: 'Clause 7.2',
      section: '7',
      title: 'Competence',
    },
    {
      externalId: 'ISO22301-7.3',
      scfId: 'SCF-MSS-003',
      ref: 'Clause 7.3',
      section: '7',
      title: 'Awareness',
    },
    {
      externalId: 'ISO22301-7.4',
      scfId: 'SCF-MSS-003',
      ref: 'Clause 7.4',
      section: '7',
      title: 'Communication',
    },
    {
      externalId: 'ISO22301-7.5',
      scfId: 'SCF-GOV-011',
      ref: 'Clause 7.5',
      section: '7',
      title: 'Documented information',
    },

    // ── Clause 8 — Operation ───────────────────────────────────────────
    {
      externalId: 'ISO22301-8.1',
      scfId: 'SCF-MSS-004',
      ref: 'Clause 8.1',
      section: '8',
      title: 'Operational planning and control',
    },
    {
      externalId: 'ISO22301-8.2',
      scfId: 'SCF-RSK-001',
      ref: 'Clause 8.2',
      section: '8',
      title: 'Business impact analysis and risk assessment',
    },
    {
      primary: true,
      externalId: 'ISO22301-8.2',
      scfId: 'SCF-BCM-004',
      ref: 'Clause 8.2',
      section: '8',
      title: 'Business impact analysis and risk assessment',
    }, // primary — business impact analysis (drives RTO/RPO)
    {
      externalId: 'ISO22301-8.3',
      scfId: 'SCF-BCM-003',
      ref: 'Clause 8.3',
      section: '8',
      title: 'Business continuity strategies and solutions',
    },
    {
      externalId: 'ISO22301-8.3',
      scfId: 'SCF-OPS-011',
      ref: 'Clause 8.3',
      section: '8',
      title: 'Business continuity strategies and solutions',
    },
    {
      primary: true,
      externalId: 'ISO22301-8.3',
      scfId: 'SCF-BCM-001',
      ref: 'Clause 8.3',
      section: '8',
      title: 'Business continuity strategies and solutions',
    }, // primary
    {
      externalId: 'ISO22301-8.4',
      scfId: 'SCF-INC-001',
      ref: 'Clause 8.4',
      section: '8',
      title: 'Business continuity plans and procedures',
    },
    {
      externalId: 'ISO22301-8.4',
      scfId: 'SCF-BCM-002',
      ref: 'Clause 8.4',
      section: '8',
      title: 'Business continuity plans and procedures',
    },
    {
      primary: true,
      externalId: 'ISO22301-8.4',
      scfId: 'SCF-BCM-001',
      ref: 'Clause 8.4',
      section: '8',
      title: 'Business continuity plans and procedures',
    }, // primary
    {
      externalId: 'ISO22301-8.5',
      scfId: 'SCF-BCM-005',
      ref: 'Clause 8.5',
      section: '8',
      title: 'Exercise programme',
    },
    {
      externalId: 'ISO22301-8.6',
      scfId: 'SCF-MSS-005',
      ref: 'Clause 8.6',
      section: '8',
      title: 'Evaluation of business continuity documentation and capabilities',
    },
    {
      primary: true,
      externalId: 'ISO22301-8.6',
      scfId: 'SCF-BCM-001',
      ref: 'Clause 8.6',
      section: '8',
      title: 'Evaluation of business continuity documentation and capabilities',
    }, // primary

    // ── Clause 9 — Performance evaluation ──────────────────────────────
    {
      externalId: 'ISO22301-9.1',
      scfId: 'SCF-MSS-005',
      ref: 'Clause 9.1',
      section: '9',
      title: 'Monitoring, measurement, analysis and evaluation',
    },
    {
      externalId: 'ISO22301-9.2',
      scfId: 'SCF-AUD-001',
      ref: 'Clause 9.2',
      section: '9',
      title: 'Internal audit',
    },
    {
      externalId: 'ISO22301-9.3',
      scfId: 'SCF-MSS-006',
      ref: 'Clause 9.3',
      section: '9',
      title: 'Management review',
    },

    // ── Clause 10 — Improvement ────────────────────────────────────────
    {
      externalId: 'ISO22301-10.1',
      scfId: 'SCF-MSS-007',
      ref: 'Clause 10.1',
      section: '10',
      title: 'Nonconformity and corrective action',
    },
    {
      externalId: 'ISO22301-10.2',
      scfId: 'SCF-MSS-007',
      ref: 'Clause 10.2',
      section: '10',
      title: 'Continual improvement',
    },
  ],
};

module.exports = {
  FRAMEWORK_ISO22301,
};
